import {
  Row,
  Column,
} from '@devexpress/dx-react-grid';
import {
  MainPageContainer,
  UpperInfoContainer,
} from 'components/PageLayout';
import { createReportURL } from 'core/routes';
import {
  convertToDate,
  useNpsSummaryRepairGroups,
  useNPSSummaryReportV4,
  useNpsSummarySchemeCodes,
} from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  dateToString,
  formatDate,
  formatDateTime,
} from 'utils/helpers';
import {
  RepairerMetaData,
  WorkProviderScheme,
} from 'api/resources/models/AutoGenerated';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { AxiosError } from 'axios';
import { ReportsLoader } from 'components/ReportsLoader';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { NPS_SUMMARY_REPORT, NPS_SUMMARY_REPORT_MESSAGE, NPS_SUMMARY_REPORT_TITLE, newColumns, summaryItems } from './constants';
import { calculateSelectedRow, cellPrepared, customizeCell, reportGroupingOptions, valuesConverter } from './helpers';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { NPSSummarySearchForm } from 'components/ReportForms/NPSSummarySearchForm';
import { SelectOption } from 'components/types';
import { ReportProps } from 'pages/ReportsDetails/hooks';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import ReportGridNew from 'components/ReportGrid_New';
import { onlyXLSXexport } from 'components/ReportGrid_New/constants';

export function NPSSummaryReport({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    endDate: urlParams.get('endDate'),
    schemeCodes: urlParams.get('schemeCodes'),
    noOfDateGroups: urlParams.get('noOfDateGroups'),
    repairSites: urlParams.get('repairSites'),
    reportingGroup: urlParams.get('reportingGroup'),
  };

  const queryEnabled =
    !!urlParamsValue.endDate &&
    !!urlParamsValue.repairSites &&
    !!urlParamsValue.schemeCodes &&
    !!urlParamsValue.noOfDateGroups &&
    !!urlParamsValue.reportingGroup;

  const { repairerSites, isRepairSitesLoading } = useNpsSummaryRepairGroups(
    (error) => handleError(error, 'isRepairSites')
  );
  const { siteCodes, isSiteCodesLoading } = useNpsSummarySchemeCodes((error) =>
    handleError(error, 'isSiteCodes')
  );
  const { reportData, refetchReport, isReportFetching } = useNPSSummaryReportV4(
    {
      endDate: urlParamsValue.endDate,
      repairSites: urlParamsValue.repairSites,
      schemeCodes: urlParamsValue.schemeCodes,
      noOfDateGroups: urlParamsValue.noOfDateGroups,
      reportingGroup: urlParamsValue.reportingGroup,
    },
    queryEnabled,
    (error) => handleError(error, 'isReport')
  );

  const nestedColumns = useMemo(() => {
    const additionalColumns: Column[] = [];
    reportData?.map((data) => {
      data.monthYearGroupings?.map((e) => {
        if (!additionalColumns.find((a) => a.name == e.name)) {
          additionalColumns.push({
            name: e.name,
            title: e.name,
          });
        }
      });
    });
    return {
      additionalNewColumns: [
        {
          title: 'Month/Year',
          children: [
            ...additionalColumns,
          ],
        },
      ]
    };
  }, [reportData]);

    const rows = useMemo<Row[] | undefined>(() => reportData ? valuesConverter(reportData) : undefined, [reportData]);
  
    const [columns, setColumns] = useState<any[]>([]);

    useEffect(() => {
    setColumns([
      ...newColumns,
      ...nestedColumns.additionalNewColumns,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParamsValue.endDate) || null
  );
  const [repairSites, setRepairSites] = useState<SelectOption[]>([]);
  const [schemeCodes, setSchemeCodes] = useState<SelectOption[]>([]);
  const [noOfDateGroups, setNoOfDateGroups] = useState<number | null>(
    Number(urlParamsValue.noOfDateGroups) || null
  );
  const [reportingGroup, setReportingGroup] = useState<string | null>(
    urlParamsValue.reportingGroup || null
  );
  const repairSiteOptions = useMemo(
    () =>
      repairerSites?.map((e: RepairerMetaData, i: number) => {
        return {
          id: i,
          value: e.siteCode || '',
          text: e.repairerName || '',
        };
      }),
    [repairerSites]
  );
  const siteCodeOptions = siteCodes?.map((e: WorkProviderScheme, i: number) => {
    return {
      id: i,
      value: e.schemeCode || '',
      text: e.schemeCode || '',
    };
  });

  const isOptionsLoading = isRepairSitesLoading || isSiteCodesLoading;

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlParamsValue.endDate,
    urlParamsValue.repairSites,
    urlParamsValue.schemeCodes,
    urlParamsValue.noOfDateGroups,
    urlParamsValue.reportingGroup,
  ]);

  useEffect(() => {
    if (
      urlParamsValue.repairSites != repairSites?.map((e) => e.value)?.join(',')
    )
      setRepairSites(
        repairSiteOptions?.filter((e) =>
          urlParamsValue.repairSites?.split(',').includes(e.value)
        ) || []
      );
    if (
      urlParamsValue.schemeCodes != repairSites?.map((e) => e.value)?.join(',')
    )
      setSchemeCodes(
        siteCodeOptions?.filter((e) =>
          urlParamsValue.schemeCodes?.split(',').includes(e.value)
        ) || []
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsLoading]);

    const reportHeaderTagLine = useMemo(
    () =>
      `Report produced on ${formatDateTime(new Date())} for ${formatDate(endDate)}`,
    [endDate]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={NPS_SUMMARY_REPORT}
          infoMessage={NPS_SUMMARY_REPORT_MESSAGE}
        />
        {isOptionsLoading ? (
          <ReportsLoader loadingRowsCount={2} />
        ) : (
            <NPSSummarySearchForm
              handleSubmit={handleSubmit}
              endDate={endDate}
              setEndDate={setEndDate}
              repairSites={repairSites}
              setRepairSites={setRepairSites}
              schemeCodes={schemeCodes}
              setSchemeCodes={setSchemeCodes}
              noOfDateGroups={noOfDateGroups}
              setNoOfDateGroups={setNoOfDateGroups}
              reportGroupingOptions={reportGroupingOptions}
              repairSiteOptions={repairSiteOptions}
              siteCodeOptions={siteCodeOptions}
              reportingGroup={reportingGroup}
              setReportingGroup={setReportingGroup}
              isDisabled={isReportFetching}
            />
        )}
      </UpperInfoContainer>

      {!isReportFetching && rows?.length === 0 && (
        <NoReportDataMessage />
      )}

      <ReportGridContainer direction="column">
        <ReportGridNew
          calculateSelectedRow={calculateSelectedRow}
          cellPrepared={cellPrepared}
          columns={columns}
          customizeCell={customizeCell}
          exportFormats={onlyXLSXexport}
          headerTitleForSheet={NPS_SUMMARY_REPORT}
          isLoading={isReportFetching}
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          summaryItems={summaryItems}
          title={NPS_SUMMARY_REPORT_TITLE}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParamsValue.endDate == dateToString(endDate) &&
      urlParamsValue.repairSites ==
        repairSites?.map((e) => e.value)?.join(',') &&
      urlParamsValue.schemeCodes ==
        schemeCodes?.map((e) => e.value)?.join(',') &&
      urlParamsValue.noOfDateGroups == noOfDateGroups?.toString() &&
      urlParamsValue.reportingGroup == reportingGroup
    )
      refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (
      !!endDate &&
      repairSites?.length > 0 &&
      !!schemeCodes &&
      !!noOfDateGroups &&
      !!reportingGroup
    )
      navigate(
        createReportURL(
          {
            endDate: dateToString(endDate),
            repairSites: repairSites?.map((e) => e.value)?.join(','),
            schemeCodes: schemeCodes?.map((e) => e.value)?.join(','),
            noOfDateGroups: noOfDateGroups?.toString(),
            reportingGroup: reportingGroup,
          },
          reportPath,
        ), { replace: true });
        
    else return;
  }

  function handleError(
    error: AxiosError,
    type: 'isReport' | 'isRepairSites' | 'isSiteCodes'
  ) {
    let errorMessage; 
    switch (type) {
      case 'isRepairSites':
        errorMessage = errorMessages.repairSites;
        break;
      case 'isSiteCodes':
        errorMessage = errorMessages.schemeCodes;
        break;
      default:
        errorMessage = errorMessages.reports('NPS Summary Report V4');
    }

    setErrorMessage(errorMessage);
    setToast(true);
    reportLogger(error);
  }
}